<template>
  <div>
    <v-row class="mx-0 mb-4">
      <v-spacer/>
      <v-btn class="mt-8 mt-md-0" @click="startNewRow" v-if="!newRowActive && editIndex === null">
        Agregar tipo de cliente
      </v-btn>
    </v-row>

    <v-data-table
      :headers="headers"
      :items="clientTypes"
      item-value="id"
      class="elevation-0"
      hide-default-footer
    >
      <template #header="{ props }">
        <tr>
          <th v-for="header in props.headers" :key="header.value">
            {{ header.text }}
          </th>
        </tr>
      </template>

      <!-- NUEVA FILA -->
      <template v-slot:[`body.prepend`]>
        <tr v-if="newRowActive">
          <td>
            <v-text-field
              v-model="newRow.name"
              label="Nombre"
              dense
              hide-details
              variant="solo"
            />
          </td>
          <td>
            <v-text-field
              v-model="newRow.tag"
              label="Tag"
              dense
              hide-details
              variant="solo"
            />
          </td>
          <td style="text-align:right;">
            <v-btn variant="text" size="small" @click="cancelNewRow">
              Cancelar
            </v-btn>
            <v-btn size="small" @click="saveNewRow">
              Guardar
            </v-btn>
          </td>
        </tr>
      </template>

      <!-- FILAS NORMALES Y EDITABLES -->
      <template #item="{ item, index }">
        <tr>
          <td v-if="editIndex === index">
            <v-text-field
              v-model="editItem.name"
              label="Nombre"
              dense
              hide-details
              variant="solo"
            />
          </td>
          <td v-else>{{ item.name }}</td>

          <td v-if="editIndex === index">
            <v-text-field
              v-model="editItem.tag"
              label="Tag"
              dense
              hide-details
              variant="solo"
            />
          </td>
          <td v-else>{{ item.tag }}</td>

          <td style="text-align:right;">
            <v-btn
              v-if="editIndex !== index"
              icon
              variant="text"
              size="x-small"
              @click="startEditRow(index, item)"
            >
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
            <template v-else>
              <v-btn variant="text" size="small" @click="cancelEdit">
                Cancelar
              </v-btn>
              <v-btn size="small" class="mr-2" @click="saveEditRow(item)">
                Guardar
              </v-btn>
            </template>
            <v-btn
              icon
              variant="text"
              size="x-small"
              @click="deleteRow(item)"
              v-if="editIndex !== index"
            >
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </td>
        </tr>
      </template>
    </v-data-table>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, computed } from 'vue';
import { useClientTypeStore } from '@/store/clientTypes';
import { useBottomSheetStore } from '@/store/bottomSheet';

export default defineComponent({
  name: 'ClientTypesTable',
  setup() {
    const bottomSheetStore = useBottomSheetStore();
    const clientTypeStore = useClientTypeStore();

    const newRowActive = ref(false);
    const newRow = ref<{ name: string; tag: string }>({ name: '', tag: '' });
    const editIndex = ref<number | null>(null);
    const editItem = ref<any>({});

    const headers = [
      { text: 'Nombre', value: 'name' },
      { text: 'Tag', value: 'tag' },
      { text: '', value: 'actions' }
    ];

    const clientTypes = computed(() =>
      clientTypeStore.client_types.map((t: any) => ({
        id: t.id,
        name: t.name || t.attributes?.name,
        tag: t.tag || t.attributes?.tag
      }))
    );

    const refresh = async () => {
      await clientTypeStore.fetchClientTypes({});
      newRowActive.value = false;
      editIndex.value = null;
      newRow.value = { name: '', tag: '' };
      editItem.value = {};
    };

    const startNewRow = () => {
      newRowActive.value = true;
      newRow.value = { name: '', tag: '' };
      editIndex.value = null;
    };

    const cancelNewRow = () => {
      newRowActive.value = false;
      newRow.value = { name: '', tag: '' };
    };

    const saveNewRow = async () => {
      if (!newRow.value.name) return;
      await clientTypeStore.createClientType(newRow.value);
      await refresh();
    };

    const startEditRow = (index: number, item: any) => {
      editIndex.value = index;
      editItem.value = { ...item };
      newRowActive.value = false;
    };

    const cancelEdit = () => {
      editIndex.value = null;
      editItem.value = {};
    };

    const saveEditRow = async (item: any) => {
      if (!editItem.value.name) return;
      await clientTypeStore.updateClientType(item.id, editItem.value);
      await refresh();
    };

    const deleteRow = (item: any) => {
      bottomSheetStore.openSheet({
        module: 'ClientTypes',
        formTitle: `¿Seguro(a) que deseas eliminar "${item.name}"?`,
        props: {
          item,
          message: 'Esta acción no se puede deshacer.',
          action: async () => {
            await clientTypeStore.deleteClientType(item.id);
            await refresh();
          }
        }
      });
    };

    onMounted(refresh);

    return {
      // state
      newRowActive,
      newRow,
      editIndex,
      editItem,
      headers,
      clientTypes,

      // methods
      refresh,
      startNewRow,
      cancelNewRow,
      saveNewRow,
      startEditRow,
      cancelEdit,
      saveEditRow,
      deleteRow,
    };
  },
});
</script>
